import { useEffect, useRef, useState, type MutableRefObject } from "react";
import L from "leaflet";
import apiClient from "@/api/client";
import { useSignedWmsUrl } from "@/features/tasks/hooks/useSignedWmsUrl";

/**
 * Raw-DXF WMS overlay for the formatki plan editor — the extracted MapTab
 * recipe (MapTab.tsx "Raw mode" effect): fetch the QGZ layer list, exclude the
 * curated "parcels" layer, order basemap → mapa zasadnicza → dxf, and attach a
 * signed-WMS tile layer. Toggleable; the layer is removed when disabled so no
 * tiles are fetched.
 */
export function useRawDxfWms(
  mapRef: MutableRefObject<L.Map | null>,
  gisProjectId: string | null | undefined,
  enabled: boolean,
) {
  const { data: wms } = useSignedWmsUrl(enabled ? gisProjectId ?? undefined : undefined);
  const layerRef = useRef<L.TileLayer.WMS | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (layerRef.current) {
      map.removeLayer(layerRef.current);
      layerRef.current = null;
    }
    setLoading(false);
    if (!enabled || !gisProjectId || !wms?.wms_url) return;
    let cancelled = false;
    apiClient
      .get<{ data: { layers?: { name: string }[] } }>(`/gis/projects/${gisProjectId}/`)
      .then((r) => {
        if (cancelled || !mapRef.current) return;
        const ord = (n: string) => (n === "basemap" ? 0 : n === "dxf" ? 2 : 1);
        const names = (r.data.data.layers || [])
          .map((l) => l.name)
          .filter((n) => n !== "parcels")
          .sort((a, b) => ord(a) - ord(b))
          .join(",");
        if (!names) return;
        const layer = L.tileLayer.wms(wms.wms_url, {
          layers: names,
          format: "image/png",
          transparent: true,
          version: "1.3.0",
          tileSize: 512,
          maxZoom: 21,
          zIndex: 1,
        });
        layer.on("loading", () => setLoading(true));
        layer.on("load", () => setLoading(false));
        layer.addTo(mapRef.current);
        setLoading(true);
        layerRef.current = layer;
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [mapRef, wms, gisProjectId, enabled]);

  return { loading, available: !!gisProjectId };
}
